import React, { Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { useAuth } from '../../hooks/useAuth';
import { Button, UserAvatar } from '../common';

// Hamburger icon component
const HamburgerIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

// Chevron down icon component
const ChevronDownIcon = () => (
  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

export const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.svg" alt="FAD" className="h-8 w-8" />
            <span className="text-2xl font-bold text-primary-600">FAD</span>
          </Link>

          {/* Desktop navigation */}
          <nav className="hidden sm:flex items-center gap-4">
            <Link
              to="/about"
              className="text-gray-600 hover:text-gray-900 px-3 py-2"
            >
              About
            </Link>
            {isAuthenticated ? (
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100">
                  <UserAvatar
                    displayName={user?.displayName}
                    avatarUrl={user?.avatarUrl}
                    size="xs"
                  />
                  <span className="text-gray-700">@{user?.displayName}</span>
                  <ChevronDownIcon />
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/dashboard"
                          className={`block px-4 py-2 text-sm ${
                            active ? 'bg-gray-100' : ''
                          }`}
                        >
                          Dashboard
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/settings"
                          className={`block px-4 py-2 text-sm ${
                            active ? 'bg-gray-100' : ''
                          }`}
                        >
                          Settings
                        </Link>
                      )}
                    </Menu.Item>
                    <hr className="my-1 border-gray-100" />
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`block w-full text-left px-4 py-2 text-sm text-red-600 ${
                            active ? 'bg-gray-100' : ''
                          }`}
                        >
                          Log Out
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2"
                >
                  Log In
                </Link>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/register')}
                >
                  Sign Up
                </Button>
              </>
            )}
          </nav>

          {/* Mobile navigation */}
          <Menu as="div" className="relative sm:hidden">
            <Menu.Button className="p-2 rounded-lg hover:bg-gray-100">
              <HamburgerIcon />
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/about"
                      className={`block px-4 py-2 text-sm ${
                        active ? 'bg-gray-100' : ''
                      }`}
                    >
                      About
                    </Link>
                  )}
                </Menu.Item>
                <hr className="my-1 border-gray-100" />
                {isAuthenticated ? (
                  <>
                    <div className="px-4 py-2 flex items-center gap-2">
                      <UserAvatar
                        displayName={user?.displayName}
                        avatarUrl={user?.avatarUrl}
                        size="sm"
                      />
                      <span className="text-xs text-gray-500">@{user?.displayName}</span>
                    </div>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/dashboard"
                          className={`block px-4 py-2 text-sm ${
                            active ? 'bg-gray-100' : ''
                          }`}
                        >
                          Dashboard
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/settings"
                          className={`block px-4 py-2 text-sm ${
                            active ? 'bg-gray-100' : ''
                          }`}
                        >
                          Settings
                        </Link>
                      )}
                    </Menu.Item>
                    <hr className="my-1 border-gray-100" />
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`block w-full text-left px-4 py-2 text-sm text-red-600 ${
                            active ? 'bg-gray-100' : ''
                          }`}
                        >
                          Log Out
                        </button>
                      )}
                    </Menu.Item>
                  </>
                ) : (
                  <>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/login"
                          className={`block px-4 py-2 text-sm ${
                            active ? 'bg-gray-100' : ''
                          }`}
                        >
                          Log In
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/register"
                          className={`block px-4 py-2 text-sm font-medium text-primary-600 ${
                            active ? 'bg-gray-100' : ''
                          }`}
                        >
                          Sign Up
                        </Link>
                      )}
                    </Menu.Item>
                  </>
                )}
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </header>
  );
};
